import React, { useState, useEffect } from 'react';
import Popup from '../Popup';
import './ImportCustomerPlaylist.scss';
import "../../pages/EditCustomerPlaylist/EditCustomerPlaylist.scss"
import { Checkbox } from '@mui/material';
import search from '../../assets/ic_search.png';
import RoundButton from '../RoundButton';
import { useDispatch, useSelector } from 'react-redux';
import { duplicateCustomerPlaylist } from '../../api/duplicateCustomerPlaylist';

const ImportCustomerPlaylist = ({ open, onClose, currentCustomer }) => {
  const dispatch = useDispatch();
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [searchInput, setSearchInput] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const [includeSchedule, setIncludeSchedule] = useState(true);

  // Reset all state values when popup is closed
  const handleClose = () => {
    setSelectedCustomer(null);
    setSearchInput('');
    setIsFocused(false);
    onClose();

  };

  // Get filtered customer list from Redux store
  const customerList = useSelector((state) => state.customerList.result);

  useEffect(() => {
    if (!open) return;
    // Load all customers once; search is done client-side (same as CustomerList page)
    dispatch({ type: 'CUSTOMER_LIST', payload: '' });
  }, [open, dispatch]);

  const getCustomerName = (customer) => {
    if (customer.name) {
      return customer.name;
    } else {
      let name = '';
      name = customer.firstname || '';
      name = name + ' ' + customer.lastname || '';
      return name || 'N/A';
    }
  };

  // Client-side search filter - same matching logic as CustomerList page
  const filterCustomers = (customers) => {
    if (!searchInput.trim() || !Array.isArray(customers)) return customers;

    const searchTerm = searchInput.toLowerCase().trim();

    return customers.filter((c) => {
      const customerName = getCustomerName(c).toLowerCase();
      const businessName = (c.business_name || '').toLowerCase();
      const email = (c.email || '').toLowerCase();
      const address = (c.address || '').toLowerCase();
      const phone = (c.phone || '').toLowerCase();
      const username = (c.username || '').toLowerCase();

      return (
        customerName.includes(searchTerm) ||
        businessName.includes(searchTerm) ||
        email.includes(searchTerm) ||
        address.includes(searchTerm) ||
        phone.includes(searchTerm) ||
        username.includes(searchTerm)
      );
    });
  };

  // Filter out the current customer, then apply search filter
  const filteredCustomerList = filterCustomers(
    Array.isArray(customerList) ? customerList.filter((customer) => customer.id !== currentCustomer?.id) : []
  );

  const handleCustomerSelect = (customer) => {
    setSelectedCustomer(customer);
    setIsFocused(false);
  };

  const onHandleFocus = () => {
    setIsFocused(true);
  };

  const onHandleBlur = (event) => {
    // Only hide the list when clicking outside the customer list container
    if (!event.relatedTarget?.closest('.customer-list-container')) {
      setIsFocused(false);
    }
  };

  const handleImportPlaylist = async () => {
    if (!selectedCustomer) return;
    try {
      await duplicateCustomerPlaylist({
        fromCustomerId: selectedCustomer.id,
        toCustomerId: currentCustomer.id,
        includeSchedule,
      }, currentCustomer.id);

      dispatch({ type: 'GET_CUSTOMER_PLAYLIST', payload: { customerId: currentCustomer.id } });

    } catch (error) {
      console.log("error===>", error)
    }
    handleClose();
  }

  return (
    <Popup open={open} onClose={handleClose} dialogClassName="ImportCustomerPlaylist">
      <div className="import-customer-playlist-content">
        <div className="import-customer-playlist-content-header">
          <h4>Import Playlist & Scheduler</h4>
        </div>
        <div className="import-customer-playlist-item-container">
          <p className='input-label' >Import From:</p>
          <div className="customer-search-input-container">
            <div className="search_row search_playlist">
              <input
                className="input_field search_input input-search-customer"
                placeholder="Search"
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onFocus={onHandleFocus}
                onBlur={onHandleBlur}
              />
              <img src={search} alt="search" />
              {isFocused && (
                <div className='customer-list-container' >
                  {filteredCustomerList.map((customer) => (
                    <div
                      className="customer-list-item-popup"
                      key={customer.id}
                      onClick={() => handleCustomerSelect(customer)}
                      style={{ cursor: 'pointer' }}
                    >
                      <Checkbox
                        color="primary"
                        checked={selectedCustomer?.id === customer.id}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleCustomerSelect(customer);
                        }}
                      />
                      <div className="customer-suggestion-text">
                        <p className="customer-suggestion-business">{customer.business_name || 'N/A'}</p>
                        <p className="customer-suggestion-name">{getCustomerName(customer)}</p>
                      </div>
                    </div>
                  ))}
                  {filteredCustomerList.length === 0 && (
                    <div className="no-results">No customers found</div>
                  )}
                </div>
              )}
            </div>
            {selectedCustomer && (
              <div className='selected-customer-container'>
                <div className='selected-customer-item'>
                  <Checkbox
                    color="primary"
                    checked={true}
                    onChange={() => setSelectedCustomer(null)}
                  />
                  <p className='customer-list-item-name'>{getCustomerName(selectedCustomer)}</p>
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="import-customer-playlist-import-container">
          <p className='input-label' >Import To:</p>
          <div className="customer-import-to-container">
            <div className="customer-list-item">
              <Checkbox
                color="primary"
                checked={includeSchedule}
                onChange={() => setIncludeSchedule(!includeSchedule)}
              />
              <p className='customer-list-item-name' >Playlists & Scheduler</p>
            </div>
            <div className="customer-list-item">
              <Checkbox
                color="primary"
                checked={!includeSchedule}
                onChange={() => setIncludeSchedule(!includeSchedule)}
              />
              <p className='customer-list-item-name' >Playlists</p>
            </div>
          </div>
        </div>
        <p className='warning-text'>
          <span className='warning-text-title' >Warning: </span>
          If playlist is already is scheduled for the same time importing a new playlist will override the existing schedule.
        </p>
        <div className="import-playlist-btn-container">
          <RoundButton
            btnText="Import"
            handleClick={handleImportPlaylist}
            className="import-playlist-btn"
            disabled={!selectedCustomer}
          />
        </div>
      </div>
    </Popup>
  );
};

export default ImportCustomerPlaylist;

